/**
 * i18n Configuration
 *
 * Configures react-i18next for multi-language support
 * Supports 4 languages: English (en), German (de), French (fr), Italian (it)
 *
 * TASK-167: Frontend i18n Infrastructure Setup
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import English translations
import enCommon from '../locales/en/common.json';
import enNavigation from '../locales/en/navigation.json';
import enAuth from '../locales/en/auth.json';
import enDashboard from '../locales/en/dashboard.json';
import enMeeting from '../locales/en/meeting.json';
import enPortfolio from '../locales/en/portfolio.json';
import enCompliance from '../locales/en/compliance.json';
import enClients from '../locales/en/clients.json';
import enSettings from '../locales/en/settings.json';

// Import German translations
import deCommon from '../locales/de/common.json';
import deNavigation from '../locales/de/navigation.json';
import deAuth from '../locales/de/auth.json';
import deDashboard from '../locales/de/dashboard.json';
import deMeeting from '../locales/de/meeting.json';
import dePortfolio from '../locales/de/portfolio.json';
import deCompliance from '../locales/de/compliance.json';
import deClients from '../locales/de/clients.json';
import deSettings from '../locales/de/settings.json';

// Import French translations
import frCommon from '../locales/fr/common.json';
import frNavigation from '../locales/fr/navigation.json';
import frAuth from '../locales/fr/auth.json';
import frDashboard from '../locales/fr/dashboard.json';
import frMeeting from '../locales/fr/meeting.json';
import frPortfolio from '../locales/fr/portfolio.json';
import frCompliance from '../locales/fr/compliance.json';
import frClients from '../locales/fr/clients.json';
import frSettings from '../locales/fr/settings.json';

// Import Italian translations
import itCommon from '../locales/it/common.json';
import itNavigation from '../locales/it/navigation.json';
import itAuth from '../locales/it/auth.json';
import itDashboard from '../locales/it/dashboard.json';
import itMeeting from '../locales/it/meeting.json';
import itPortfolio from '../locales/it/portfolio.json';
import itCompliance from '../locales/it/compliance.json';
import itClients from '../locales/it/clients.json';
import itSettings from '../locales/it/settings.json';

// Resource object with all translations
const resources = {
  en: {
    common: enCommon,
    navigation: enNavigation,
    auth: enAuth,
    dashboard: enDashboard,
    meeting: enMeeting,
    portfolio: enPortfolio,
    compliance: enCompliance,
    clients: enClients,
    settings: enSettings,
  },
  de: {
    common: deCommon,
    navigation: deNavigation,
    auth: deAuth,
    dashboard: deDashboard,
    meeting: deMeeting,
    portfolio: dePortfolio,
    compliance: deCompliance,
    clients: deClients,
    settings: deSettings,
  },
  fr: {
    common: frCommon,
    navigation: frNavigation,
    auth: frAuth,
    dashboard: frDashboard,
    meeting: frMeeting,
    portfolio: frPortfolio,
    compliance: frCompliance,
    clients: frClients,
    settings: frSettings,
  },
  it: {
    common: itCommon,
    navigation: itNavigation,
    auth: itAuth,
    dashboard: itDashboard,
    meeting: itMeeting,
    portfolio: itPortfolio,
    compliance: itCompliance,
    clients: itClients,
    settings: itSettings,
  },
};

// Initialize i18next
i18n
  .use(LanguageDetector) // Detect user language from browser
  .use(initReactI18next) // Pass i18n instance to react-i18next
  .init({
    resources,
    fallbackLng: 'de', // Fallback to German (primary Swiss language) for European date formats
    defaultNS: 'common', // Default namespace
    ns: [
      'common',
      'navigation',
      'auth',
      'dashboard',
      'meeting',
      'portfolio',
      'compliance',
      'clients',
      'settings',
    ],

    interpolation: {
      escapeValue: false, // React already escapes by default
    },

    react: {
      useSuspense: false, // Don't use Suspense for async loading
    },

    // Language detection configuration
    detection: {
      order: ['localStorage', 'navigator'], // Check localStorage first, then browser
      caches: ['localStorage'], // Cache selected language in localStorage
      lookupLocalStorage: 'i18nextLng', // IMPORTANT: Different from useMultiLanguage ('preferredLanguage')
    },

    // Enable debug mode in development
    debug: import.meta.env.MODE === 'development',
  });

export default i18n;
